import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl/dist/mapbox-gl";
import { useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useDisclosure } from "@chakra-ui/hooks";
import { MdFileDownload, MdNorthEast } from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button from "components/button";
import Card from "components/card";
import { SearchInput, Select } from "components/fields";
import Loader from "components/loader/Loader";
import { PageContainer, PageHeader } from "components/page";
import { EmptyState, ErrorState } from "components/states";
import { useDarkmode } from "hooks/useTheme";
import { palette, withAlpha } from "lib/chartTheme";
import { roleTone } from "lib/statusTone";
import UserDetailsDrawer from "views/admin/people/user-details";

// One drive-time scale shared by the marker rings, the legend chips and the
// campus zones on the map. Zones are approximated as circles around the campus
// (roughly 15 min of city driving ≈ 10 miles, 30 min ≈ 20 miles).
const DRIVE_TIME_BANDS = [
  { id: "near", label: "Under 15 min", color: palette.sage, zoneRadiusMiles: 10 },
  { id: "mid", label: "15 to 30 min", color: palette.gold, zoneRadiusMiles: 20 },
  { id: "far", label: "Over 30 min", color: palette.rose },
];

const ZONE_BANDS = DRIVE_TIME_BANDS.filter((band) => band.zoneRadiusMiles);

const driveTimeBand = (minutes) => {
  if (minutes < 15) return DRIVE_TIME_BANDS[0];
  if (minutes <= 30) return DRIVE_TIME_BANDS[1];
  return DRIVE_TIME_BANDS[2];
};

const zoneLayerId = (band) => `drive-zone-${band.id}`;

const removeDriveZones = (map) => {
  ZONE_BANDS.forEach((band) => {
    const id = zoneLayerId(band);
    if (map.getLayer(id)) map.removeLayer(id);
    if (map.getSource(id)) map.removeSource(id);
  });
};

// Approximate circle polygon around [lng, lat].
const createCircle = (center, radiusInMiles, points = 64) => {
  const coords = [];
  const distanceX = radiusInMiles / (69.0 * Math.cos((center[1] * Math.PI) / 180));
  const distanceY = radiusInMiles / 69.0;

  for (let i = 0; i < points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    coords.push([
      center[0] + distanceX * Math.cos(theta),
      center[1] + distanceY * Math.sin(theta),
    ]);
  }
  coords.push(coords[0]);

  return coords;
};

const CHURCH_ICON_SVG = `<svg class="inline-block" width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 19V14.3C0 13.9 0.108333 13.5375 0.325 13.2125C0.541667 12.8875 0.833333 12.6417 1.2 12.475L4 11.225V9.225C4 8.84167 4.1 8.49583 4.3 8.1875C4.5 7.87917 4.76667 7.63333 5.1 7.45L9 5.5V4H8C7.71667 4 7.47917 3.90417 7.2875 3.7125C7.09583 3.52083 7 3.28333 7 3C7 2.71667 7.09583 2.47917 7.2875 2.2875C7.47917 2.09583 7.71667 2 8 2H9V1C9 0.716667 9.09583 0.479167 9.2875 0.2875C9.47917 0.0958333 9.71667 0 10 0C10.2833 0 10.5208 0.0958333 10.7125 0.2875C10.9042 0.479167 11 0.716667 11 1V2H12C12.2833 2 12.5208 2.09583 12.7125 2.2875C12.9042 2.47917 13 2.71667 13 3C13 3.28333 12.9042 3.52083 12.7125 3.7125C12.5208 3.90417 12.2833 4 12 4H11V5.5L14.9 7.45C15.2333 7.63333 15.5 7.87917 15.7 8.1875C15.9 8.49583 16 8.84167 16 9.225V11.225L18.8 12.475C19.1667 12.6417 19.4583 12.8875 19.675 13.2125C19.8917 13.5375 20 13.9 20 14.3V19C20 19.55 19.8042 20.0208 19.4125 20.4125C19.0208 20.8042 18.55 21 18 21H13C12.7167 21 12.4792 20.9042 12.2875 20.7125C12.0958 20.5208 12 20.2833 12 20V18C12 17.45 11.8042 16.9792 11.4125 16.5875C11.0208 16.1958 10.55 16 10 16C9.45 16 8.97917 16.1958 8.5875 16.5875C8.19583 16.9792 8 17.45 8 18V20C8 20.2833 7.90417 20.5208 7.7125 20.7125C7.52083 20.9042 7.28333 21 7 21H2C1.45 21 0.979167 20.8042 0.5875 20.4125C0.195833 20.0208 0 19.55 0 19ZM10 12.5C10.4167 12.5 10.7708 12.3542 11.0625 12.0625C11.3542 11.7708 11.5 11.4167 11.5 11C11.5 10.5833 11.3542 10.2292 11.0625 9.9375C10.7708 9.64583 10.4167 9.5 10 9.5C9.58333 9.5 9.22917 9.64583 8.9375 9.9375C8.64583 10.2292 8.5 10.5833 8.5 11C8.5 11.4167 8.64583 11.7708 8.9375 12.0625C9.22917 12.3542 9.58333 12.5 10 12.5Z" fill="currentColor"/></svg>`;

const PLACE_MARKER_CLASS =
  "flex h-12 w-12 items-center justify-center rounded-full border border-line bg-surface shadow-card";

// Member-entered fields (names, addresses) flow into popup setHTML;
// escape everything interpolated into HTML strings to prevent stored XSS
const escapeHtml = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const secondsToString = (seconds) => {
  const numhours = Math.floor(seconds / 3600);
  const numminutes = Math.floor((seconds % 3600) / 60);
  const hUnit = numhours > 0 && (numhours > 1 ? " hours " : " hour ");
  return (hUnit ? numhours + hUnit : "") + numminutes + " minutes ";
};

const placePopupHTML = ({ name, address, city, state, zip }) => `
  <div class="px-5 pt-5 pb-4">
    <h2 class="text-base font-bold text-fg">${escapeHtml(name)}</h2>
    <div class="mt-4 flex w-full min-w-60 flex-row gap-4">
      <p class="flex-auto text-sm text-fg-muted">Location</p>
      <div class="flex-1 text-sm font-bold text-fg">
        <p>${escapeHtml(address)}</p>
        <p>${escapeHtml(city)}</p>
        <p>${escapeHtml(state)} ${escapeHtml(zip)}</p>
      </div>
    </div>
  </div>`;

const isValidPair = (pair) =>
  Array.isArray(pair) &&
  pair.length === 2 &&
  !isNaN(pair[0]) &&
  !isNaN(pair[1]) &&
  isFinite(pair[0]) &&
  isFinite(pair[1]);

const MemberLocation = () => {
  const { darkmode } = useDarkmode();
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState({});
  const [campuses, setCampuses] = useState([]);
  const [church, setChurch] = useState();
  const [map, setMap] = useState();
  const [showDriveTimeColors, setShowDriveTimeColors] = useState(false);
  const [campusFilter, setCampusFilter] = useState("all");
  const [queryOptions, setQueryOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [showClustering, setShowClustering] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const campusMarkersRef = useRef([]);

  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after unmount

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await AtriumApi.get(`/v1/member-location-data`, {
          params: {
            search: queryOptions?.search,
            role: queryOptions?.role || undefined,
            driveTimeRange: queryOptions?.driveTimeRange || undefined,
            campusId: campusFilter,
          },
        });

        if (!isMounted) return;

        setUsers(resp?.data?.data?.members || []);
        setCampuses(resp?.data?.data?.campuses || []);
      } catch (err) {
        if (!isMounted) return;
        setError(err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [
    queryOptions?.driveTimeRange,
    queryOptions?.role,
    queryOptions?.search,
    campusFilter,
    reloadKey,
  ]);

  useEffect(() => {
    AtriumApi.get("/v1/church")
      .then((resp) => {
        const churchData = resp?.data?.church;
        const coordinates = churchData?.location?.coordinates;

        // Safely extract coordinates with proper validation
        let lat = null;
        let lng = null;

        if (coordinates && Array.isArray(coordinates) && coordinates.length >= 2) {
          lng = parseFloat(coordinates[0]);
          lat = parseFloat(coordinates[1]);

          if (isNaN(lng) || isNaN(lat) || !isFinite(lng) || !isFinite(lat) ||
              lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            lat = null;
            lng = null;
          }
        }

        setChurch({
          ...churchData,
          lat,
          lng,
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    const newMap = new mapboxgl.Map({
      container: "map",
      style: darkmode
        ? "mapbox://styles/mapbox/dark-v11"
        : "mapbox://styles/mapbox/light-v11",
      zoom: 10,
      minZoom: 5,
      projection: "equirectangular",
    });

    newMap.on("load", () => {
      setMap(newMap);
    });

    // The map sits in a flex-1 container whose height settles after the first
    // layout; keep the canvas in sync with it.
    const container = newMap.getContainer();
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => newMap.resize())
        : null;
    resizeObserver?.observe(container);

    return () => {
      resizeObserver?.disconnect();
      newMap.off("load");
      newMap.remove();
    };
  }, [darkmode]);

  useEffect(() => {
    if (!map || !users) return;

    // Track invalid coordinates for summary
    const missingCoords = [];
    const invalidValues = [];
    const defaultCoords = [];

    // Sample GeoJSON data with points
    const geojson = {
      type: "FeatureCollection",
      features: users
        .filter((user) => {
          // Validate coordinates exist and are valid numbers
          // Handle both location.coordinates format and direct lat/lng properties
          let lat, lng;

          if (user?.location?.coordinates && Array.isArray(user.location.coordinates) && user.location.coordinates.length === 2) {
            // GeoJSON format: [lng, lat]
            [lng, lat] = user.location.coordinates;
          } else if (user?.lat != null && user?.lng != null) {
            // Direct lat/lng properties
            lat = user.lat;
            lng = user.lng;
          } else {
            missingCoords.push(`${user?.firstName} ${user?.lastName}`);
            return false;
          }
          const isValidLat = !isNaN(lat) && isFinite(lat) && lat >= -90 && lat <= 90;
          const isValidLng = !isNaN(lng) && isFinite(lng) && lng >= -180 && lng <= 180;

          if (!isValidLat || !isValidLng) {
            invalidValues.push(`${user?.firstName} ${user?.lastName}`);
            return false;
          }

          // Filter out [0, 0] coordinates (invalid/default values)
          if (lat === 0 && lng === 0) {
            defaultCoords.push(`${user?.firstName} ${user?.lastName}`);
            return false;
          }

          return true;
        })
        .map((user) => {
          // Get coordinates based on format
          let coordinates;
          if (user?.location?.coordinates && Array.isArray(user.location.coordinates) && user.location.coordinates.length === 2) {
            coordinates = [
              parseFloat(user.location.coordinates[0]), // longitude
              parseFloat(user.location.coordinates[1]), // latitude
            ];
          } else if (user?.lat != null && user?.lng != null) {
            coordinates = [
              parseFloat(user.lng), // longitude
              parseFloat(user.lat), // latitude
            ];
          }

          return {
            type: "Feature",
            properties: user,
            geometry: {
              type: "Point",
              coordinates: coordinates,
            },
          };
        }),
    };

    // Log summary of coordinate issues (if any)
    const totalInvalid = missingCoords.length + invalidValues.length + defaultCoords.length;
    if (totalInvalid > 0) {
      console.log(`📍 Member Location Summary: ${geojson.features.length} valid, ${totalInvalid} skipped`);
      if (missingCoords.length > 0) {
        console.log(`  - ${missingCoords.length} missing address/coordinates`);
      }
      if (invalidValues.length > 0) {
        console.log(`  - ${invalidValues.length} invalid coordinate values`);
      }
      if (defaultCoords.length > 0) {
        console.log(`  - ${defaultCoords.length} default [0,0] coordinates`);
      }
    }

    const popupProfile = (event) => {
      const button = event.target?.closest("button.open-profile");
      if (!button) return;
      const indexId = button.getAttribute("data-index");

      // Handle both integer IDs and UUID strings
      let id = indexId;
      const numericId = parseInt(indexId, 10);
      if (!isNaN(numericId)) {
        id = numericId;
      }

      const index = users.findIndex((user) => user.id === id);

      setUser(users[index]);
      onOpen();
    };

    const members = [],
      sources = [];
    let churchMarker;

    // Remove existing source if it exists to update cluster property
    if (map.getSource("members")) {
      // Remove layers that depend on this source first
      if (map.getLayer("clusters")) map.removeLayer("clusters");
      if (map.getLayer("cluster-count")) map.removeLayer("cluster-count");
      if (map.getLayer("unclustered-point")) map.removeLayer("unclustered-point");
      if (map.getLayer("member-heat")) map.removeLayer("member-heat");
      map.removeSource("members");
    }

    map.addSource("members", {
      type: "geojson",
      data: geojson,
      cluster: showClustering && !showHeatmap,
      clusterMaxZoom: 12, // Max zoom to cluster points - higher = more detail
      clusterRadius: 80, // Radius of each cluster - smaller = less overlap
    });
    sources.push("members");

    // Density heatmap view on the brand sage ramp
    if (showHeatmap) {
      map.addLayer({
        id: "member-heat",
        type: "heatmap",
        source: "members",
        paint: {
          "heatmap-weight": 1,
          "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 8, 0.6, 13, 2],
          "heatmap-color": [
            "interpolate", ["linear"], ["heatmap-density"],
            0, withAlpha(palette.sage, 0),
            0.2, withAlpha(palette.sage, 0.35),
            0.45, palette.sage,
            1, palette.deep,
          ],
          "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 8, 18, 13, 36],
          "heatmap-opacity": 0.85,
        },
      });
      sources.push("member-heat");
    }

    // Small groups sage, big clusters deep sage
    map.addLayer({
      id: "clusters",
      type: "circle",
      source: "members",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": ["step", ["get", "point_count"], palette.sage, 30, palette.deep],
        "circle-radius": ["step", ["get", "point_count"], 18, 10, 24, 30, 30, 75, 36],
        "circle-stroke-width": 3,
        "circle-stroke-color": "white",
        "circle-opacity": 0.92,
      },
    });
    sources.push("clusters");

    map.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "members",
      filter: ["has", "point_count"],
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 14,
      },
      paint: {
        "text-color": "white",
        "text-halo-color": palette.deep,
        "text-halo-width": 1,
      },
    });
    sources.push("cluster-count");

    map.addLayer({
      id: "unclustered-point",
      type: "circle",
      source: "members",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": palette.sage,
        "circle-radius": 8,
        "circle-stroke-width": 2,
        "circle-stroke-color": "white",
        "circle-opacity": 0.9,
      },
    });
    sources.push("unclustered-point");

    const churchLatLng = [church?.lng, church?.lat];

    // Render HTML elements for un-clustered members (hidden in heatmap view)
    !showHeatmap && geojson.features.forEach((feature) => {
      const coordinates = feature.geometry.coordinates;
      if (!isValidPair(coordinates)) {
        return;
      }

      const { firstName, lastName, role, address, zip, city, state, id } =
        feature.properties;

      // Drive time is computed server-side when the org is geocoded
      const driveTime = feature.properties.driveTime;
      const band = driveTime ? driveTimeBand(driveTime) : null;

      // In Drive Time View the marker ring doubles as the distance signal
      const el = document.createElement("div");
      el.className = "rounded-full bg-surface shadow-lg";
      el.style.border = `3px solid ${
        showDriveTimeColors && band ? band.color : "white"
      }`;
      el.innerHTML = ReactDOMServer.renderToString(
        <Avatar user={feature.properties} size="md" noBorder className="block" />
      );

      const driveTimeDisplay = band
        ? ReactDOMServer.renderToString(
            <Badge swatch={band.color}>
              {`${Math.round(driveTime)} min drive`}
            </Badge>
          )
        : `<span class="text-xs text-fg-muted">Open to see route</span>`;

      // The profile button's data-index is numeric only: it is interpolated into HTML
      const memberPopupHTML = `
        <div class="px-5 pt-5 pb-4">
          <div class="flex flex-row items-center gap-3">
            ${ReactDOMServer.renderToString(
              <Avatar user={feature.properties} size="md" />
            )}
            <div class="flex-1">
              <h2 class="mb-1 text-base font-bold text-fg">${escapeHtml(firstName)} ${escapeHtml(lastName)}</h2>
              ${role
                ? ReactDOMServer.renderToString(
                    <Badge tone={roleTone(role)} className="capitalize">
                      {role}
                    </Badge>
                  )
                : ""}
            </div>
          </div>

          <div class="flex items-center justify-between border-b border-line py-3">
            <span class="text-xs font-medium uppercase tracking-wide text-fg-muted">Drive Time</span>
            <span class="driving-time">${driveTimeDisplay}</span>
          </div>
          <div class="py-3">
            <p class="mb-1 text-xs font-medium uppercase tracking-wide text-fg-muted">Address</p>
            <p class="text-sm font-medium leading-snug text-fg">${escapeHtml(address)}<br/>${escapeHtml(city)}${city ? "," : ""} ${escapeHtml(state)} ${escapeHtml(zip)}</p>
          </div>

          ${ReactDOMServer.renderToString(
            <Button
              fullWidth
              className="open-profile"
              data-index={Number(id) || 0}
              leftIcon={<MdNorthEast aria-hidden="true" />}
            >
              View Profile
            </Button>
          )}
        </div>`;

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        maxWidth: 300,
      }).setHTML(memberPopupHTML);

      const showRoutes = (event) => {
        if (!church) return;
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${churchLatLng.join(
          ","
        )};${coordinates.join(",")}?geometries=geojson&access_token=${
          mapboxgl.accessToken
        }`;
        const drivingTimeEl = event.target
          .getElement()
          .querySelector(".driving-time");
        drivingTimeEl.innerText = "Calculating…";
        fetch(url).then((response) => {
          response.json().then((data) => {
            const route = data.routes[0].geometry;
            drivingTimeEl.innerText = secondsToString(data.routes[0].duration);
            map.getLayer("route") && map.removeLayer("route");
            map.getSource("route") && map.removeSource("route");
            map.addLayer({
              id: "route",
              type: "line",
              source: {
                type: "geojson",
                data: {
                  type: "Feature",
                  properties: {},
                  geometry: route,
                },
              },
              layout: {
                "line-join": "round",
                "line-cap": "round",
              },
              paint: {
                "line-color": palette.deep,
                "line-width": 2,
                "line-dasharray": [2, 2],
              },
            });
            sources.indexOf("route") === -1 && sources.push("route");
          });
        });
      };

      popup.on("open", showRoutes);

      const marker = new mapboxgl.Marker(el)
        .setLngLat(coordinates)
        .setPopup(popup)
        .addTo(map);

      members.push(marker);
    });

    // update visibility of markers according clustering
    function updateMarkerVisibility() {
      members.forEach((marker) => {
        const features = map.queryRenderedFeatures(
          map.project(marker.getLngLat()),
          {
            layers: ["unclustered-point"],
          }
        );
        const element = marker.getElement();
        element.style.display = features.length === 0 ? "none" : "block";
      });
    }

    const churchEl = document.createElement("div");

    // add church point
    if (church) {
      map.setZoom(12);
      if (isValidPair(churchLatLng)) {
        map.setCenter(churchLatLng);
      } else {
        // Geographic center of the US when church coordinates are invalid
        map.setCenter([-98.5795, 39.8283]);
      }
      updateChurchMarkerSize();
      map.on("zoom", updateChurchMarkerSize);
      churchEl.className = `${PLACE_MARKER_CLASS} text-primary dark:text-accent`;
      churchEl.innerHTML = CHURCH_ICON_SVG;

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        maxWidth: 300,
      }).setHTML(placePopupHTML(church));

      if (isValidPair(churchLatLng)) {
        churchMarker = new mapboxgl.Marker(churchEl)
          .setLngLat(churchLatLng)
          .setPopup(popup)
          .addTo(map);
      }
    }

    // Fit the view to the core of the congregation. Bounds are computed from
    // points near the median center (90th percentile distance, 1.5x slack) so
    // a single bad geocode or far-away family can't zoom the map out to the
    // whole country; outliers stay on the map, they just don't frame it.
    {
      const pts = [];
      geojson.features.forEach((f) => {
        const c = f.geometry?.coordinates;
        if (c && isFinite(c[0]) && isFinite(c[1])) pts.push(c);
      });
      const churchPt =
        churchLatLng && isFinite(churchLatLng[0]) && isFinite(churchLatLng[1])
          ? churchLatLng
          : null;

      if (pts.length || churchPt) {
        let core = pts;
        if (pts.length >= 3) {
          const mid = (arr) => arr.sort((a, b) => a - b)[Math.floor(arr.length / 2)];
          const center = [mid(pts.map((p) => p[0])), mid(pts.map((p) => p[1]))];
          const dist = (p) => Math.hypot(p[0] - center[0], p[1] - center[1]);
          const sorted = pts.map(dist).sort((a, b) => a - b);
          const p90 = sorted[Math.min(Math.floor(sorted.length * 0.9), sorted.length - 1)];
          const cutoff = Math.max(p90 * 1.5, 0.5); // never tighter than ~35 miles
          core = pts.filter((p) => dist(p) <= cutoff);
          // Anchor on the church only when it's actually near the congregation
          if (churchPt && dist(churchPt) <= cutoff) core.push(churchPt);
        } else if (churchPt) {
          core = [...pts, churchPt];
        }

        const bounds = new mapboxgl.LngLatBounds();
        core.forEach((c) => bounds.extend(c));
        map.fitBounds(bounds, { padding: 80, maxZoom: 13, duration: 800 });
      }
    }

    campusMarkersRef.current.forEach((marker) => marker.remove());
    campusMarkersRef.current = [];

    // add campuses
    campuses?.forEach((campus) => {
      const campusLatLng = [campus?.coordinates?.[0], campus?.coordinates?.[1]];

      const campusEl = document.createElement("div");
      campusEl.className = `${PLACE_MARKER_CLASS} text-brand-600`;
      campusEl.innerHTML = CHURCH_ICON_SVG;

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        maxWidth: 300,
      }).setHTML(placePopupHTML({ ...campus, zip: campus?.zipCode }));

      if (isValidPair(campusLatLng)) {
        const marker = new mapboxgl.Marker(campusEl)
          .setLngLat(campusLatLng)
          .setPopup(popup)
          .addTo(map);

        campusMarkersRef.current.push(marker);
      }
    });

    // Drive time zones when Drive Time View is on and a campus is selected
    removeDriveZones(map);
    if (showDriveTimeColors && campusFilter !== "all") {
      const selectedCampus = campuses?.find((c) => c.id === parseInt(campusFilter));

      if (selectedCampus && selectedCampus.location?.coordinates) {
        const center = selectedCampus.location.coordinates;
        let innerRing = null;

        ZONE_BANDS.forEach((band) => {
          const ring = createCircle(center, band.zoneRadiusMiles);
          const id = zoneLayerId(band);

          // Each zone is a band: the smaller zone is cut out of the larger one
          map.addSource(id, {
            type: "geojson",
            data: {
              type: "Feature",
              geometry: {
                type: "Polygon",
                coordinates: innerRing ? [ring, [...innerRing].reverse()] : [ring],
              },
            },
          });

          map.addLayer(
            {
              id,
              type: "fill",
              source: id,
              paint: {
                "fill-color": band.color,
                "fill-outline-color": band.color,
                "fill-opacity": 0.15,
              },
            },
            "unclustered-point"
          );

          sources.push(id);
          innerRing = ring;
        });
      }
    }


    // 2. Function to update marker size
    function updateChurchMarkerSize() {
      if (!churchMarker) return;
      const zoom = map.getZoom();
      const size = getChurchMarkerSize(zoom);
      churchEl.style.width = `${size}px`;
      churchEl.style.height = `${size}px`;
      // Optionally adjust inner SVG size as well
      const svg = churchEl.querySelector("svg");
      if (svg) {
        svg.style.width = `${Math.round(size * 0.6)}px`;
        svg.style.height = `${Math.round(size * 0.6)}px`;
      }
    }

    // 1. Function to get marker size based on zoom
    function getChurchMarkerSize(zoom) {
      // Tweak these values as needed
      const minSize = 20; // px
      const maxSize = 48; // px (original size)
      const minZoom = 5;
      const maxZoom = 18;
      // Linear interpolation between min and max
      if (zoom <= minZoom) return minSize;
      if (zoom >= maxZoom) return maxSize;
      return (
        minSize + ((zoom - minZoom) / (maxZoom - minZoom)) * (maxSize - minSize)
      );
    }

    map.on("move", updateMarkerVisibility);
    map.on("zoom", updateMarkerVisibility);
    map.on("render", updateMarkerVisibility);

    updateMarkerVisibility();

    map.getContainer().addEventListener("click", popupProfile);

    return () => {
      // Remove event listeners
      try {
        if (map.getContainer()) {
          map.getContainer().removeEventListener("click", popupProfile);
        }
      } catch (e) {
        // Silently fail
      }

      // Check if map is still valid
      if (!map.getCanvasContainer()) {
        return;
      }

      // Clean up member markers
      members.forEach((marker) => {
        try {
          marker.remove();
        } catch (e) {
          // Silently fail
        }
      });

      // Clean up church marker
      if (churchMarker) {
        try {
          churchMarker.remove();
        } catch (e) {
          // Silently fail
        }
      }

      // Clean up campus markers
      campusMarkersRef.current.forEach((marker) => {
        try {
          marker.remove();
        } catch (e) {
          // Silently fail
        }
      });
      campusMarkersRef.current = [];

      // Remove map event listeners
      try {
        map.off("move", updateMarkerVisibility);
        map.off("zoom", updateMarkerVisibility);
        map.off("render", updateMarkerVisibility);
        map.off("zoom", updateChurchMarkerSize);
      } catch (e) {
        // Silently fail
      }

      // Clean up layers first, then sources (layers must be removed before their source)
      sources.forEach((id) => {
        try {
          if (map.getLayer && map.getLayer(id)) {
            map.removeLayer(id);
          }
        } catch (e) {
          // Silently fail
        }
      });
      sources.forEach((id) => {
        try {
          if (map.getSource && map.getSource(id)) {
            map.removeSource(id);
          }
        } catch (e) {
          // Silently fail
        }
      });
    };
  }, [church, map, onOpen, users, campuses, showClustering, showHeatmap, showDriveTimeColors, campusFilter]);

  // Center map on selected campus when filter changes
  useEffect(() => {
    if (!map || !campusFilter || campusFilter === "all") return;

    const selectedCampus = campuses.find(
      (campus) => campus.id === parseInt(campusFilter)
    );

    if (selectedCampus && selectedCampus.coordinates) {
      const [lng, lat] = selectedCampus.coordinates;

      if (!isNaN(lng) && !isNaN(lat) && isFinite(lng) && isFinite(lat)) {
        map.flyTo({
          center: [lng, lat],
          zoom: 13,
          essential: true,
          duration: 1500,
        });
      }
    }
  }, [campusFilter, campuses, map]);

  const exportFilteredCsv = () => {
    if (!users.length) return;
    const esc = (v) => {
      let str = String(v ?? "");
      // Neutralize spreadsheet formula injection from member-entered fields
      if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
      return `"${str.replace(/"/g, '""')}"`;
    };
    const rows = [
      ["First Name", "Last Name", "Role", "Email", "Phone", "Address", "City", "State", "Zip", "Drive Time (min)"],
      ...users.map((u) => [
        u.firstName, u.lastName, u.role, u.email, u.phone,
        u.address, u.city, u.state, u.zip,
        u.driveTime ?? "",
      ]),
    ];
    const blob = new Blob([rows.map((r) => r.map(esc).join(",")).join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `member-locations-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const filtersActive = Boolean(
    queryOptions.search ||
      queryOptions.role ||
      queryOptions.driveTimeRange ||
      campusFilter !== "all"
  );
  const mappedCount = users.filter((u) => u.location?.coordinates).length;
  const missingCount = users.length - mappedCount;
  const mapView = showHeatmap ? "heatmap" : showDriveTimeColors ? "drivetime" : "normal";

  return (
    <PageContainer variant="bleed" className="gap-5">
      <PageHeader
        title="Member Location"
        actions={
          <Button
            variant="secondary"
            leftIcon={<MdFileDownload aria-hidden="true" />}
            onClick={exportFilteredCsv}
            disabled={!users.length}
            title="Download the currently filtered members as CSV"
          >
            Export CSV
          </Button>
        }
      />

      <Card padding="sm" className="gap-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
          <SearchInput
            value={queryOptions.search || ""}
            onChange={(search) => setQueryOptions((prev) => ({ ...prev, search }))}
            delay={600}
            placeholder="Search by name"
            ariaLabel="Search members by name"
            className="w-full lg:w-72"
          />

          <Select
            aria-label="Campus"
            className="w-full lg:w-auto"
            value={campusFilter}
            onChange={(e) => setCampusFilter(e.target.value)}
          >
            <option value="all">All Campuses</option>
            {campuses.map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.name}
              </option>
            ))}
          </Select>

          <Select
            aria-label="Map view"
            className="w-full lg:w-auto"
            value={mapView}
            onChange={(e) => {
              setShowHeatmap(e.target.value === "heatmap");
              setShowDriveTimeColors(e.target.value === "drivetime");
            }}
            options={[
              { value: "normal", label: "Normal View" },
              { value: "drivetime", label: "Drive Time View" },
              { value: "heatmap", label: "Heatmap" },
            ]}
          />

          <Select
            aria-label="Role"
            className="w-full lg:w-auto"
            value={queryOptions.role || ""}
            onChange={(e) => {
              const role = e.target.value;
              setQueryOptions((prev) => ({ ...prev, role }));
            }}
            options={[
              { value: "", label: "All Roles" },
              { value: "staff", label: "Staff" },
              { value: "volunteer", label: "Volunteers" },
              { value: "member", label: "Members" },
              { value: "guest", label: "Guests" },
            ]}
          />

          <Select
            aria-label="Drive time"
            className="w-full lg:w-auto"
            value={queryOptions.driveTimeRange || ""}
            onChange={(e) => {
              const driveTimeRange = e.target.value;
              setQueryOptions((prev) => ({ ...prev, driveTimeRange }));
            }}
            options={[
              { value: "", label: "Any Distance" },
              { value: "0-5", label: "Under 5 min" },
              { value: "6-10", label: "6 to 10 min" },
              { value: "11-20", label: "11 to 20 min" },
              { value: "21-30", label: "21 to 30 min" },
              { value: "31-45", label: "31 to 45 min" },
              { value: "46-60", label: "46 to 60 min" },
              { value: "61", label: "Over 60 min" },
            ]}
          />

          <Select
            aria-label="Marker display"
            className="w-full lg:w-auto"
            value={showClustering ? "clustered" : "individual"}
            onChange={(e) => setShowClustering(e.target.value === "clustered")}
            options={[
              { value: "clustered", label: "Clustered" },
              { value: "individual", label: "Individual" },
            ]}
          />
        </div>

        {(showDriveTimeColors || users.length > 0) && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-fg-muted">
            {showDriveTimeColors && (
              <ul aria-label="Drive time colors" className="flex flex-wrap items-center gap-3">
                {DRIVE_TIME_BANDS.map((band) => (
                  <li key={band.id} className="flex items-center gap-1.5">
                    <span
                      aria-hidden="true"
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: band.color }}
                    />
                    {band.label}
                  </li>
                ))}
              </ul>
            )}

            {users.length > 0 && (
              <p>
                <span className="font-bold text-fg">{users.length} shown</span>
                {" "}· {mappedCount} mapped
                {missingCount > 0 && (
                  <span
                    className="text-warning-fg"
                    title="Members without a geocoded home address, often missing from Planning Center"
                  >
                    {" "}· {missingCount} missing address{missingCount === 1 ? "" : "es"}
                  </span>
                )}
              </p>
            )}
          </div>
        )}
      </Card>

      <div className="relative flex min-h-[480px] flex-1 flex-col overflow-hidden rounded-card">
        {/* mapbox-gl.css gives .mapboxgl-map position: relative, so the map fills via flex. */}
        <div id="map" className="min-h-0 flex-1" />

        {showDriveTimeColors && campusFilter !== "all" && (
          <Card padding="sm" className="absolute right-4 top-4 z-10 max-w-xs">
            <h2 className="text-sm font-semibold text-fg">Drive Time Zones</h2>
            <p className="mt-1 text-xs text-fg-muted">
              Estimated driving distance from campus
            </p>
            <ul className="mt-3 space-y-2">
              {ZONE_BANDS.map((band) => (
                <li key={band.id} className="flex items-center gap-2 text-xs text-fg">
                  <span
                    aria-hidden="true"
                    className="h-4 w-4 rounded border"
                    style={{
                      backgroundColor: withAlpha(band.color, 0.35),
                      borderColor: band.color,
                    }}
                  />
                  {band.label}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {!loading && !error && users.length === 0 && (
          <Card padding="sm" className="absolute left-4 top-4 z-10 max-w-xs">
            <EmptyState
              variant="inline"
              title={
                filtersActive
                  ? "No members match your filters"
                  : "No member locations yet"
              }
            />
          </Card>
        )}

        {error && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-surface/80 p-4">
            <Card padding="md" className="w-full max-w-md">
              <ErrorState
                message={getApiErrorMessage(error, "Could not load member locations.")}
                onRetry={() => setReloadKey((key) => key + 1)}
              />
            </Card>
          </div>
        )}

        {loading && <Loader variant="overlay" label="Loading member locations" />}
      </div>

      <UserDetailsDrawer isOpen={isOpen} onClose={onClose} user={user} />
    </PageContainer>
  );
};

export default MemberLocation;
